/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:04]
Last time the file was updated (UTC): [27.01.2024 4:51:28]
Permissions to update outside the main branch: [true]
*/

import { Button, DropDown, Tab, useErrorState } from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import { useTradeOrgWorkplaceState } from '../TradeOrgs';
import { TradeOrgAddForm } from './TradeOrgChangeModal';
import TradeOrgChangeModalTabApi from './TradeOrgChangeModalTabApi';
import TradeOrgChangeModalTabContactFaces from './TradeOrgChangeModalTabContactFaces';
import TradeOrgChangeModalTabMain from './TradeOrgChangeModalTabMain';
import TradeOrgChangeModalTabSettings from './TradeOrgChangeModalTabSettings';

const TradeOrgChangeModalForm = ({
    currentActiveTab,
    closeModal,
    toggleTab,
    showAdditionalFields
}: {
    currentActiveTab: string;
    closeModal: () => void;
        toggleTab: (tab: string) => void;
        showAdditionalFields: boolean;
}) => {
    const { t } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const { currentItem } = useTradeOrgWorkplaceState();

    const { handleRequestError } = useErrorState();

    const [productCategories, setProductCategories] = useState<DropDown[]>([]);
    const [integrationTypes, setIntegrationTypes] = useState<DropDown[]>([]);
    const [notifySellers, setNotifySellers] = useState<DropDown[]>([]);
    const [orderStatuses, setOrderStatuses] = useState<DropDown[]>([]);
    const [settingsTypes, setSettingsTypes] = useState<DropDown[]>([]);

    const { watch } = useFormContext<TradeOrgAddForm>();
    const integrationType = watch('integrationType');

    const { isFetching } = useQuery(
        ['tradeOrgsModalDicts'],
        () =>
            getSelectList(api, [
                'ProductCategory',
                'TOIntegrationType',
                'OrderStatus',
                'TradeOrgSettingTypes',
                'NotifyConfirmedOrder',
            ]),
        {
            onSuccess: (data) => {
                if (data) {
                    data['ProductCategory'] && setProductCategories(data['ProductCategory']);
                    data['TOIntegrationType'] && setIntegrationTypes(data['TOIntegrationType']);
                    data['OrderStatus'] &&
                        setOrderStatuses(
                            data['OrderStatus'].filter(
                                (d) => ['signed', 'goodship'].indexOf(d.id?.toString() ?? '') >= 0
                            )
                        );
                    data['TradeOrgSettingTypes'] && setSettingsTypes(data['TradeOrgSettingTypes']);
                    data['NotifyConfirmedOrder'] && setNotifySellers(data['NotifyConfirmedOrder']);
                }
            },
            onError: (err) => {
                handleRequestError(err);
            },
            refetchOnWindowFocus: false,
        }
    );

    const name = currentItem ? `${currentItem.legalEntityName}` : t('BackOfficeNew');

    const formHasRole = useMemo(
        () => (currentItem ? hasRole('tradeOrgsUpdate') : hasRole('tradeOrgsCreate')),
        [hasRole]
    );

    return (
        <>
            <ModalHeader toggle={closeModal}>{name}</ModalHeader>
            <ModalBody className="custom-scrollbar">
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => toggleTab('main')}
                            active={currentActiveTab === 'main'}
                        >
                            {t('BackOfficeMainPageName')}
                        </NavLink>
                    </NavItem>
                    {hasRole('tradeOrgsChangeSettings') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => toggleTab('settings')}
                                active={currentActiveTab === 'settings'}
                            >
                                {t('BackOfficeSettings')}
                            </NavLink>
                        </NavItem>
                    )}
                    {showAdditionalFields && integrationType && integrationType === 'API' && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => toggleTab('apiSettings')}
                                active={currentActiveTab === 'apiSettings'}
                            >
                                {t('BackOfficeApiSettings')}
                            </NavLink>
                        </NavItem>
                    )}
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => toggleTab('contactFaces')}
                            active={currentActiveTab === 'contactFaces'}
                        >
                            {t('BackOfficeContactFaces')}
                        </NavLink>
                    </NavItem>
                </Nav>
                <TabContent activeTab={currentActiveTab}>
                    <Tab toggle={(tab) => toggleTab(tab?.toString() ?? '')} tabId="main">
                        <TradeOrgChangeModalTabMain
                            productCategories={productCategories || []}
                            integrationTypes={integrationTypes || []}
                            notifySellers={notifySellers || []}
                            orderStatuses={orderStatuses || []}
                            dictsLoading={isFetching}
                            formHasRole={formHasRole}
                            showAdditionalFields={showAdditionalFields}
                        />
                    </Tab>
                    {hasRole('tradeOrgsChangeSettings') && (
                        <Tab toggle={(tab) => toggleTab(tab?.toString() ?? '')} tabId="settings">
                            <TradeOrgChangeModalTabSettings
                                settingTypes={settingsTypes || []}
                                settingTypesFetching={isFetching}
                                formHasRole={formHasRole}
                            />
                        </Tab>
                    )}
                    {showAdditionalFields && integrationType && integrationType === 'API' && (
                        <Tab toggle={(tab) => toggleTab(tab?.toString() ?? '')} tabId="apiSettings">
                            <TradeOrgChangeModalTabApi formHasRole={formHasRole} />
                        </Tab>
                    )}

                    <Tab toggle={(tab) => toggleTab(tab?.toString() ?? '')} tabId="contactFaces">
                        <TradeOrgChangeModalTabContactFaces formHasRole={formHasRole} />
                    </Tab>
                </TabContent>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" type="button" onClick={closeModal}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                <Button color="success" type="submit" disabled={!formHasRole}>
                    <Trans i18nKey="BackOfficeSave" />
                </Button>
            </ModalFooter>
        </>
    );
};

export default TradeOrgChangeModalForm;
